import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function EmployeeVehicleTab({ employeeId }) {
  const [assignments, setAssignments] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState('');

  useEffect(() => {
    fetchAssignments();
    fetchAvailableVehicles();
  }, [employeeId]);

  const fetchAssignments = async () => {
    try {
      const response = await apiFetch(`/api/employee-vehicles/employee/${employeeId}`);
      const data = await response.json();
      setAssignments(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setLoading(false);
    }
  };

  const fetchAvailableVehicles = async () => {
    try {
      const response = await apiFetch('/api/vehicles/locations');
      const data = await response.json();
      setAvailableVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleAssignVehicle = async () => {
    if (!selectedVehicle) return;

    const vehicle = availableVehicles.find(v => v.device_id === selectedVehicle);
    if (!vehicle) return;

    try {
      const response = await apiFetch('/api/employee-vehicles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employeeId,
          device_id: vehicle.device_id,
          vehicle_name: vehicle.name,
          is_primary: assignments.length === 0 // First vehicle is primary
        })
      });

      if (response.ok) {
        fetchAssignments();
        setShowAssignModal(false);
        setSelectedVehicle('');
      }
    } catch (error) {
      console.error('Error assigning vehicle:', error);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this vehicle assignment?')) return;

    try {
      const response = await apiFetch(`/api/employee-vehicles/${assignmentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };

  const openInMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Assigned Vehicles</h3>
        <button
          onClick={() => setShowAssignModal(true)}
          className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400 transition"
        >
          + Assign Vehicle
        </button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No vehicles assigned</p>
          <p className="text-sm text-gray-400 mt-2">Click "Assign Vehicle" to add one</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {assignments.map((assignment) => {
            const vehicle = availableVehicles.find(v => v.device_id === assignment.device_id);
            
            return (
              <div key={assignment.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="text-lg font-semibold">{assignment.vehicle_name}</h4>
                      {assignment.is_primary && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Primary
                        </span>
                      )}
                    </div>
                    
                    {vehicle ? (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Status</p>
                          <p className="text-gray-900">
                            {vehicle.speed > 0 ? `Moving (${vehicle.speed} km/h)` : 'Stationary'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Battery</p>
                          <p className="text-gray-900">
                            {vehicle.battery}/6 {vehicle.charging && '⚡'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Location</p>
                          <p className="font-mono text-xs text-gray-700">
                            {vehicle.coords.lat.toFixed(4)}, {vehicle.coords.lon.toFixed(4)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Last Update</p>
                          <p className="text-gray-900 text-xs">
                            {new Date(vehicle.last_updated).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Vehicle data not available</p>
                    )}
                  </div>

                  <div className="ml-4 flex flex-col space-y-2">
                    {vehicle && (
                      <button
                        onClick={() => openInMaps(vehicle.coords.lat, vehicle.coords.lon)}
                        className="bg-gray-300 text-black px-3 py-1 rounded text-sm hover:bg-gray-400"
                      >
                        📍 View on Map
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {assignment.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">{assignment.notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Vehicle Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Assign Vehicle</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Vehicle
              </label>
              <select
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">-- Choose a vehicle --</option>
                {availableVehicles.map(vehicle => (
                  <option key={vehicle.device_id} value={vehicle.device_id}>
                    {vehicle.name} ({vehicle.category})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleAssignVehicle}
                disabled={!selectedVehicle}
                className="flex-1 bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Assign
              </button>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedVehicle('');
                }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeeVehicleTab;
